/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, CheckCircleIcon, LinkIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import Spinner from './Spinner';
import { PortfolioItem, VoucherDetails } from '../types';

interface VoucherPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchase: (details: VoucherDetails) => Promise<{ success: boolean; voucherCode: string }>;
  services: PortfolioItem[];
}

const VoucherPurchaseModal: React.FC<VoucherPurchaseModalProps> = ({ isOpen, onClose, onPurchase, services }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<VoucherDetails>({
    serviceIds: [],
    recipientName: '',
    recipientEmail: '',
    message: '',
  });
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');

  const resetState = useCallback(() => {
    setFormData({ serviceIds: [], recipientName: '', recipientEmail: '', message: '' });
    setIsPurchasing(false);
    setPurchaseSuccess(false);
    setVoucherCode('');
    setCopyStatus('idle');
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (serviceId: string) => {
    setFormData(prev => {
      const newServiceIds = new Set(prev.serviceIds);
      if (newServiceIds.has(serviceId)) {
        newServiceIds.delete(serviceId);
      } else {
        newServiceIds.add(serviceId);
      }
      return { ...prev, serviceIds: Array.from(newServiceIds) };
    });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsPurchasing(true);
    try {
      const result = await onPurchase(formData);
      if (result.success) {
        setVoucherCode(result.voucherCode);
        setPurchaseSuccess(true);
      }
    } catch (error) {
      console.error("Purchase failed", error);
      // In a real app, you'd show an error message to the user
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleCopyCode = () => {
    if (voucherCode) {
      navigator.clipboard.writeText(voucherCode);
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  };

  const isFormValid = (formData.serviceIds?.length || 0) > 0 && (formData.recipientName?.trim() || '') && formData.recipientEmail.trim() && /\S+@\S+\.\S+/.test(formData.recipientEmail);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg flex flex-col shadow-xl border border-gray-200 dark:border-gray-700 max-h-[90vh]"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-serif tracking-wider text-gray-800 dark:text-gray-100">
                {purchaseSuccess ? t('voucher_modal_success_title') : t('voucher_modal_title')}
              </h2>
              <button
                onClick={handleClose}
                className="p-1 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                aria-label={t('common_close')}
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>

            <AnimatePresence mode="wait">
              {purchaseSuccess ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-8 text-center flex flex-col items-center"
                >
                  <CheckCircleIcon className="w-16 h-16 text-green-500 mb-4" />
                  <p className="text-gray-600 dark:text-gray-300 mb-4">{t('voucher_modal_success_message')}</p>
                  <div className="flex items-center justify-center gap-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <span className="font-mono text-lg font-bold text-gray-800 dark:text-gray-100">{voucherCode}</span>
                    <button onClick={handleCopyCode} className="flex items-center text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
                      <LinkIcon className="w-4 h-4 mr-1" />
                      {copyStatus === 'copied' ? t('common_copied') : t('voucher_modal_copy_code')}
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.form key="form" id="voucher-form" onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('voucher_modal_service_label')}</label>
                    <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar border rounded-lg p-3 dark:border-gray-600">
                      {services.map(s => (
                        <label key={s.id} className="flex items-center space-x-3 cursor-pointer p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
                          <input
                            type="checkbox"
                            checked={formData.serviceIds?.includes(s.id) || false}
                            onChange={() => handleServiceChange(s.id)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-800 dark:text-gray-200">{t(s.titleKey)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="recipientName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('voucher_modal_recipient_name_label')}</label>
                      <input type="text" id="recipientName" name="recipientName" value={formData.recipientName} onChange={handleChange} placeholder={t('voucher_modal_recipient_name_placeholder')} required className="mt-1 w-full p-3 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600" />
                    </div>
                    <div>
                      <label htmlFor="recipientEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('voucher_modal_recipient_email_label')}</label>
                      <input type="email" id="recipientEmail" name="recipientEmail" value={formData.recipientEmail} onChange={handleChange} placeholder={t('voucher_modal_recipient_email_placeholder')} required className="mt-1 w-full p-3 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('voucher_modal_message_label')}</label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} placeholder={t('voucher_modal_message_placeholder')} className="mt-1 w-full p-3 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 resize-y" />
                  </div>
                </motion.form>
              )}
            </AnimatePresence>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              {purchaseSuccess ? (
                <button onClick={handleClose} className="bg-indigo-600 text-white font-semibold py-2 px-5 rounded-lg hover:bg-indigo-700">{t('common_close')}</button>
              ) : (
                <button
                  type="submit"
                  form="voucher-form"
                  disabled={isPurchasing || !isFormValid}
                  className="flex items-center justify-center bg-gray-900 text-white font-semibold py-2 px-5 rounded-lg transition-colors hover:bg-gray-700 disabled:opacity-50 dark:bg-indigo-600 dark:hover:bg-indigo-500"
                >
                  {isPurchasing ? <Spinner className="w-5 h-5 mr-2" /> : null}
                  {isPurchasing ? t('voucher_modal_purchasing') : t('voucher_modal_purchase_button')}
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VoucherPurchaseModal;